import { createElement } from "/elements_functions/new_elements.js";
import { createNewCommentElement } from "/commentaries/new_coment.js";
import { getCommentariesForNode } from "/commentaries/get_functions.js";
import { createCommentary } from "/commentaries/commentary_functions.js";
import { createCommentGroupWithPreloadedComments } from "/commentaries/comment_group.js";
import {  SITE_URL, API_GATEWAY } from "/constants/API_constants.js";
import { insertPlaqueAndAlertWindow } from "/alert/alert_functions.js";
import { createLoadingScreen } from "/loading_screen_functions/loading_screen_functions.js";
import { ParamValidator } from '/validation/param_validator.js';

const MAX_COMMENT_LENGTH = 10000;

const commentFunctionsParam = {
    newCommentElementFunction : newCommentElementFunction,
    submitCommentFunction : submitCommentFunction,
    commentGroupInitFunction : commentGroupInitFunction,
    commentaryInitFunction : commentaryInitFunction,
    loadOriginalCommentariesButtonInitFunction : loadOriginalCommentariesButtonInitFunction,
    fetchCommentariesInCommentGroupFunction : fetchCommentariesInCommentGroupFunction,
    newReplyInitFunction : newReplyInitFunction,
    submitReply : submitReply,
    loadRepliesButtonInitFunction : loadRepliesButtonInitFunction
}

export function createBranchCommentSectionWithObserverLoader(branchId) {
    
    const commentSection = createElement('section', 'branch-comment-section', null);
    const commentSectionCaption = createElement('h6', 'branch-comment-section-caption', null);
    commentSectionCaption.innerText = 'Comment section';
    
    const loadingScreen = createLoadingScreen(100, 100, 100);
    loadingScreen.loadingCaption.remove();

    // Set up IntersectionObserver to trigger when the loader becomes visible
    const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // When the loader is visible, load and insert posts
                createCommentSectionElements(branchId).then(commentElements => {
                    commentSection.append(...commentElements);
                    loadingScreen.remove();
                });
                observer.unobserve(entry.target); // Stop observing after triggering
            }
        });
    });

    observer.observe(loadingScreen); // Start observing the loader element

    commentSection.append(commentSectionCaption);
    commentSection.append(loadingScreen);
    
    return commentSection;

}

export async function createBranchCommentSectionWithPreloadedComments(branchId) {    
    const commentSection = createElement('section', 'branch-comment-section', null);
    
    const elements = [];
    
    const commentSectionCaption = createElement('h6', 'branch-comment-section-caption', null);
    commentSectionCaption.innerText = 'Comment section';
    elements.push(commentSectionCaption);

    const commentElements = await createCommentSectionElements(branchId);

    elements.push(...commentElements);

    commentSection.append(...elements);

    return commentSection;
}

async function createCommentSectionElements(branchId) {
    const commentariesInfoPromise = getCommentariesForNode(branchId, 1);
    
    const elements = [];
    
    const loginToken = localStorage.getItem('branchof_access_token');

    const originalSubmitReply = commentFunctionsParam.submitReply;
    commentFunctionsParam.submitReply = (message, newReplyElement, parentId) => originalSubmitReply(message, newReplyElement, parentId, loginToken);
    
    const originalCommentaryInitFunction = commentFunctionsParam.commentaryInitFunction;
    commentFunctionsParam.commentaryInitFunction = (commentElement) => originalCommentaryInitFunction(commentElement, loginToken);
    
    const originalFetchCommentariesInCommentGroupFunction = commentFunctionsParam.fetchCommentariesInCommentGroupFunction;
    commentFunctionsParam.fetchCommentariesInCommentGroupFunction = (page) => originalFetchCommentariesInCommentGroupFunction(page, branchId);

    const commentariesInfo = await commentariesInfoPromise;
    

    const commentsGroup = await createCommentGroupWithPreloadedComments(commentariesInfo, commentFunctionsParam, 2);

    if(loginToken!==null && loginToken!==null) {
        const newCommentElement = createNewCommentElement((message, newCommentElement) => commentFunctionsParam.submitCommentFunction(message, newCommentElement, branchId, loginToken, commentsGroup),
            commentFunctionsParam.newCommentElementFunction);

        newCommentElement.cancelButton.remove();

        elements.push(newCommentElement);
    }

    elements.push(commentsGroup);

    return elements;
}

function newCommentElementFunction(newCommentElement) {
    newCommentElement.id = 'branch-comment-new-original';
    newCommentElement.classList.add('branch-comment-new-comment');

    const textarea = newCommentElement.textArea

    textarea.maxlength = 5000;
    textarea.placeholder = 'Add a comment...';
}

async function submitCommentFunction(message, newCommentElement, branchId, loginToken, commentGroup) {

    if(!ParamValidator.isSafeTextNullable(message, MAX_COMMENT_LENGTH)) {        
        insertPlaqueAndAlertWindow('Your comment is either too long or contains inappropriate characters');
        return;
    }

    const loadingCircleScreen = createLoadingScreen(25, 25, 50);
    loadingCircleScreen.loadingCaption.remove();

    const submitButton = newCommentElement.submitButton;

    submitButton.classList.add('hidden-info');
    newCommentElement.buttonsGroup.append(loadingCircleScreen);

    const url = new URL(API_GATEWAY+"commentaries/create/original");
    url.searchParams.append("branchId", branchId);
    url.searchParams.append("message", message);

    const response = await fetch(url, {
        method : 'POST',
        headers : {
            "Token-Auth": loginToken
        }
    });

    const status = response.status;

    if(status===200) {
        const userInfo = {
            profilePic : {
                picPath : null
            },
            username : localStorage.getItem('branchof_access_username'),
            accountName : null,
            userStatus : null
        }

        const commentInfo = {
            authorInfo : userInfo,
            message : message,
            repliesIds : null,
            date : new Date()
        }
        const newComment = createCommentary(commentInfo);

        const noCommentsCaption = commentGroup.noCommentsCaption;

        if(noCommentsCaption!==null && noCommentsCaption!==undefined)
            noCommentsCaption.remove();

        commentGroup.insertAdjacentElement('afterbegin', newComment);
    } else if (status===401) {
        insertPlaqueAndAlertWindow('Your login session has expired.');
    }

    setTimeout(() => {
        submitButton.classList.remove('hidden-info');
        loadingCircleScreen.remove();
    }, 500);
}

function commentGroupInitFunction(commentGroup) {
    commentGroup.id = ('branch-comment-section-group');
}

function commentaryInitFunction(commentElement, loginToken) {
    commentElement.userShortInfoGroup.classList.add("branch-comment-user-pic-and-names");
    commentElement.userProfilePic.classList.add("branch-comment-user-pic");
    commentElement.userNameAndUsername.classList.add("branch-comment-name-and-username");
    commentElement.message.classList.add("branch-comment-message");
    commentElement.dateAndButtons.classList.add("branch-comment-date-and-buttons");
    commentElement.date.classList.add("branch-comment-date");
    commentElement.replyButton.classList.add("branch-comment-reply-button");
    commentElement.repliesSection.classList.add("branch-comment-reply-section");

    if(loginToken===null || loginToken===undefined)
        commentElement.replyButton.remove();
}

function loadOriginalCommentariesButtonInitFunction(loadCommentariesButton) {
    loadCommentariesButton.classList.add('branch-comment-load-original-button');
}

async function fetchCommentariesInCommentGroupFunction(page, branchId) {
    const data = await getCommentariesForNode(branchId, page);

    console.log("fetchCommentariesInCommentGroupFunction" , data);
    

    if(data===null)
        return [];

    return data;
}


function newReplyInitFunction(newReply) {
    newReply.classList.add('branch-comment-new-reply');
    newReply.classList.add('branch-comment-new-comment');
    

    const textarea = newReply.textArea;

    textarea.maxlength = 5000;
    textarea.placeholder = 'I agree with you! <3';
}

async function submitReply(message, newReplyElement, parentId, loginToken) {
    const url = new URL(API_GATEWAY+"commentaries/create/reply");
    url.searchParams.append("parentId", parentId);
    url.searchParams.append("message", message);

    const response = await fetch(url, {
        method : 'POST',
        headers : {
            "Token-Auth": loginToken
        }
    });

    const status = response.status;

    if(status===200) {
        const userInfo = {
            profilePic : {
                picPath : null
            },
            username : localStorage.getItem('branchof_access_username'),
            accountName : null,
            userStatus : null
        }

        const commentInfo = {
            authorInfo : userInfo,
            message : message,
            repliesIds : null,
            date : new Date()
        }
        const newComment = createCommentary(commentInfo);
        newReplyElement.insertAdjacentElement('afterend', newComment);
        newReplyElement.remove();

    } else if (status===204) {
        insertPlaqueAndAlertWindow('The commentary you are replying to doesn\'t exist');
    } else if (status===401) {
        insertPlaqueAndAlertWindow('Your login session has expired.');
    }
}

function loadRepliesButtonInitFunction(loadRepliesButton) {
    loadRepliesButton.classList.add('branch-comment-load-replies-button');
}